import { useField } from "formik";
import React, { ReactNode, useMemo } from "react";
import { ThemeProviderV2, MultipleReactSelect, TitleReactSelect, Field, FieldLabel, InlineNotification} from '@blueprint/ui';
import { Form } from "react-bootstrap";
import {
    FormatOptionLabelMeta,
    MenuPosition,
    OptionsType,
    ValueType,
} from "react-select";
import styled from "styled-components";

import { GenericFieldProps } from "./common";
import { SelectOption } from "./Select";

type SelectFieldProps = {
    options: SelectOption[];
    disabled?: boolean;
    isMulti?: boolean;
    onChange?: (value: SelectOption | OptionsType<SelectOption> | null) => void;
    formatOptionLabel?: (
        option: SelectOption,
        labelMeta: FormatOptionLabelMeta<SelectOption, boolean>,
    ) => ReactNode;
    menuPosition?: MenuPosition;
    ariaLabelledBy?: string;
    required?: boolean;
    defaultValue?: SelectOption;
} & GenericFieldProps;

const BaseSelectField = ({
    className,
    name,
    label,
    options,
    disabled,
    autoFocus,
    isMulti,
    menuPosition,
    onChange,
    formatOptionLabel,
    ariaLabelledBy,
    required,
    defaultValue,
}: SelectFieldProps): JSX.Element => {
    const [field, meta, helpers] = useField(name);
    const sortedOptions = useMemo<SelectOption[]>(
        () => [...options].sort((a, b) => a.label?.localeCompare(b.label)),
        [options],
    );

    // react-select only clear the internal value when value is null
    // https://github.com/JedWatson/react-select/issues/3066#issuecomment-643000641
    const value = field.value
        ? isMulti
            ? options.filter((it) => field.value.includes(it.value))
            : options.find((it) => it.value === field.value)
        : null;

    const handleChange: (value: ValueType<SelectOption, boolean>) => void =
        isMulti
            ? (options) =>
                  options &&
                  helpers.setValue(
                      (options as SelectOption[]).map((it) => it.value),
                  )
            : (option) =>
                  option && helpers.setValue((option as SelectOption).value);
    const ariaLabel = isMulti ?
        options.filter((it) => field.value.includes(it.value)) &&
            options.filter((it) => field.value.includes(it.value)).length > 0 ?
                `${options.filter((it) => field.value.includes(it.value)).length} ${label} items selected` :
                label
        : label;
    return (
        <Form.Floating className={`position-relative mb-4 ${className || ""}`}>
            <ThemeProviderV2>
                <Field>
                    <FieldLabel marginBottom='6px' htmlFor={name} disabled={disabled}>{label}</FieldLabel>
                    {isMulti &&
                    <MultipleReactSelect
                        id={name}
                        options={sortedOptions}
                        value={value}
                        onChange={(value) => {
                            handleChange(value);
                            onChange && onChange(value);
                        }}
                        isInvalid={!!meta.error}
                        isDisabled={disabled}
                        autoFocus={autoFocus}
                        formatOptionLabel={formatOptionLabel}
                        width="100%"
                        aria-label={ariaLabel}
                        aria-describedby={`notification-help-${name}`}
                        required={required}
                        defaultValue={defaultValue}
                    >
                    </MultipleReactSelect>
                    }
                    {!isMulti &&
                    <TitleReactSelect
                        id={name}
                        options={sortedOptions}
                        value={value}
                        onChange={(value) => {
                            handleChange(value);
                            onChange && onChange(value);
                        }}
                        isInvalid={!!meta.error}
                        isDisabled={disabled}
                        autoFocus={autoFocus}
                        formatOptionLabel={formatOptionLabel}
                        width="100%"
                        aria-label={label}
                        aria-describedby={`notification-help-${name}`}
                        required={required}
                        defaultValue={defaultValue}
                    >
                    </TitleReactSelect>
                    }
                </Field>
            {meta.error && (
                <InlineNotification variant="warning" id={`notification-help-${name}`}>{meta.error}</InlineNotification>
            )}
            </ThemeProviderV2>
        </Form.Floating>
    );
};

const SelectField = styled(BaseSelectField)<{
    tall?: boolean;
}>`
    ${(props) =>
        props.tall &&
        `
        .form-select {
            height: 78px;
        }
        
        .select__value-container {
            height: 42px;
        }

        label {
            display: flex;
            align-items: center;
        }

        .form-select.has-value ~ label {
            display: block;
        }
    `}
`;

export default SelectField;
